import axios, { AxiosProgressEvent, AxiosResponse } from 'axios'
import { v4 } from 'uuid'
import { FileUploadType } from '@/core/enums/file-upload-type'

type Representation = {
  file: File | undefined
  completed: number
  id: string | undefined
  url: string | undefined
  error: string | undefined
}

type OnChange = (event: Representation) => void | undefined

type UploadResponse = {
  id: string
  link: string
  image: boolean
  progress: number
}

export class FileUploader {
  private static readonly CHUNK_SIZE = 100 * 1024 * 1024 // 100MB chunks
  private static readonly SINGLE_UPLOAD_ENDPOINT = '/api/upload'
  private static readonly CHUNKED_UPLOAD_ENDPOINT = '/api/upload/chunk'
  private static globalActiveUploads = 0

  private static handleBeforeUnload(e: BeforeUnloadEvent) {
    if (FileUploader.globalActiveUploads > 0) {
      e.preventDefault()
      e.returnValue = ''
    }
  }

  private file: File | undefined
  private type
  private uid // uuid used on upload
  private id: string | undefined // id from api
  private url: string | undefined // url from api
  private onChange?: OnChange
  private _progress
  private chunked: boolean
  private error: string | undefined
  private controller: AbortController | undefined

  constructor(type: FileUploadType, chunked?: boolean, onChange?: OnChange) {
    this.type = type
    this.uid = v4()
    this.onChange = onChange
    this._progress = 0
    this.chunked = chunked ?? true
  }

  representation(): Representation {
    return {
      file: this.file,
      completed: this._progress,
      error: this.error,
      id: this.id,
      url: this.url
    }
  }

  isFinished() {
    return this._progress === 100
  }

  cancel() {
    if (this.controller) {
      this.controller.abort('Operation canceled by the user.')
    }
  }

  async uploadFromFile(file: File) {
    return this.upload(file)
  }

  async uploadFromBlob(blob: Blob, name: string) {
    return this.upload(new File([blob], name))
  }

  private async upload(file: File) {
    this.controller = new AbortController()
    this.file = file
    this.error = undefined
    this._progress = 0
    this.id = undefined
    this.url = undefined

    this.reportProgress(0)

    FileUploader.globalActiveUploads++
    if (FileUploader.globalActiveUploads === 1) {
      window.addEventListener('beforeunload', FileUploader.handleBeforeUnload)
    }

    try {
      if (this.chunked) {
        await this.uploadChunked(file)
      } else {
        await this.uploadSingleRequest(file)
      }
    } catch (e: unknown) {
      this.error = (e as Error).message
    } finally {
      FileUploader.globalActiveUploads--

      if (FileUploader.globalActiveUploads <= 0) {
        FileUploader.globalActiveUploads = 0
        window.removeEventListener(
          'beforeunload',
          FileUploader.handleBeforeUnload
        )
      }

      this.reportProgress(this.error ? this._progress : 100)
    }
  }

  private reportProgress(progress: number) {
    this._progress = progress
    if (this.onChange !== undefined) {
      this.onChange(this.representation())
    }
  }

  private async uploadSingleRequest(file: File) {
    const form = new FormData()
    form.set('type', this.type)
    form.set('file', file)

    const response = await axios.post<UploadResponse>(
      FileUploader.SINGLE_UPLOAD_ENDPOINT,
      form,
      {
        signal: this.controller?.signal,
        onUploadProgress: (event: AxiosProgressEvent) => {
          const percent = Math.round((event.loaded * 100) / (event.total ?? 0))
          this.reportProgress(percent - 1) // Workaround to hold progress on post-processing
        }
      }
    )

    this.id = response.data.id
    this.url = response.data.link
  }

  private async uploadChunked(file: File) {
    const totalChunks = Math.ceil(file.size / FileUploader.CHUNK_SIZE)
    let finalResponse: AxiosResponse<UploadResponse> | undefined

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      if (this.controller?.signal.aborted) {
        throw new Error('Operation canceled by the user.')
      }

      const start = chunkIndex * FileUploader.CHUNK_SIZE
      const end = Math.min(start + FileUploader.CHUNK_SIZE, file.size)
      const chunk = file.slice(start, end)

      const form = new FormData()
      form.set('type', this.type)
      form.set('current', String(chunkIndex))
      form.set('total', String(totalChunks))
      form.set('part', chunk, file.name)
      form.set('uuid', this.uid)

      const response = await axios.post<UploadResponse>(
        FileUploader.CHUNKED_UPLOAD_ENDPOINT,
        form,
        {
          signal: this.controller?.signal,
          onUploadProgress: (event: AxiosProgressEvent) => {
            const totalLoaded =
              chunkIndex * FileUploader.CHUNK_SIZE + event.loaded
            const percent = Math.round((totalLoaded * 100) / file.size)
            this.reportProgress(percent - 1) // Workaround to hold progress on post-processing
          }
        }
      )

      if (chunkIndex === totalChunks - 1) {
        finalResponse = response
      }
    }

    if (finalResponse) {
      this.id = finalResponse.data.id
      this.url = finalResponse.data.link
    } else if (!this.controller?.signal.aborted) {
      throw new Error(
        'Chunked upload completed but no final data was received.'
      )
    }
  }
}
